import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faCopy,
} from '@fortawesome/pro-regular-svg-icons';
import { Text, Collapse, Tooltip, ActionIcon } from '@cowbell/ui/core';
import {
  getAsyncTaskUnit,
  type AsyncTaskUnit,
  type AsyncTaskUnitDetail,
} from '../../api/async-tasks.api';
import { AsyncTaskMarkdown } from './AsyncTaskMarkdown';
import { AsyncTaskUnitStatusIcon } from './AsyncTaskUnitStatusIcon';
import styles from './AsyncTaskProgress.module.css';

interface AsyncTaskUnitRowProps {
  readonly unit: AsyncTaskUnit;
  readonly runId: string;
  readonly conversationId: string | null | undefined;
}

function UnitDetailBody({
  errorText,
  resultText,
}: Readonly<{
  errorText: string | null | undefined;
  resultText: string | null | undefined;
}>) {
  const content = errorText || resultText;
  if (!content) return null;
  return (
    <div className={styles.unitDetail}>
      <AsyncTaskMarkdown
        content={content}
        tone={errorText ? 'error' : undefined}
      />
    </div>
  );
}

function getUnitCopyText(
  instruction: string,
  resultText: string | null | undefined,
  errorText: string | null | undefined
): string {
  const body = errorText || resultText;
  if (!body) {
    return instruction;
  }
  if (!instruction) {
    return body;
  }
  return `${instruction}\n\n${body}`;
}

function readUnitDetailResponse(res: unknown): AsyncTaskUnitDetail {
  const wrapped = res as { data?: AsyncTaskUnitDetail };
  return (wrapped?.data ?? res) as AsyncTaskUnitDetail;
}

/** True when the current selection intersects this header — leftover
 *  selection elsewhere in the document must not block expand. */
function selectionIntersectsNode(node: HTMLElement | null): boolean {
  if (!node) return false;
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  return (
    node.contains(range.commonAncestorContainer) || range.intersectsNode(node)
  );
}

/** One expandable unit in an async-task progress card. */
export function AsyncTaskUnitRow({
  unit,
  runId,
  conversationId,
}: AsyncTaskUnitRowProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  // Full (untruncated) result/error, fetched lazily — the run poll truncates
  // result text to keep its payload small across hundreds of units.
  const [detail, setDetail] = useState<AsyncTaskUnitDetail | null>(null);
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);
  // Finished units are expandable by STATUS, not by preview presence — the
  // poll payload truncates (and may omit) unit text, so a completed/failed
  // row must still be able to lazy-fetch its full result/error.
  const hasResultBody =
    unit.status === 'completed' ||
    unit.status === 'failed' ||
    Boolean(unit.result || unit.error);

  const loadDetail = async (): Promise<AsyncTaskUnitDetail | null> => {
    if (detail) return detail;
    if (!conversationId) return null;
    try {
      const res = await getAsyncTaskUnit(conversationId, runId, unit.index);
      const next = readUnitDetailResponse(res);
      setDetail(next);
      return next;
    } catch {
      // Truncated preview from the poll remains the fallback.
      return null;
    }
  };

  const handleToggle = () => {
    if (!hasResultBody) return;
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) {
      void loadDetail();
    }
  };

  // Whole-row click expands, but skip when the user was drag-selecting
  // instruction text inside this header so request copy still works.
  const handleHeaderClick = () => {
    if (!hasResultBody) return;
    if (selectionIntersectsNode(headerRef.current)) {
      return;
    }
    handleToggle();
  };

  const handleCopy = async () => {
    if (copying) return;
    setCopying(true);
    try {
      // Always prefer the full unit payload — poll previews are truncated.
      const full = (await loadDetail()) ?? {
        instruction: unit.instruction,
        result: unit.result,
        error: unit.error,
      };
      const text = getUnitCopyText(
        full.instruction ?? unit.instruction,
        full.result ?? unit.result,
        full.error ?? unit.error
      );
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      globalThis.setTimeout(() => setCopied(false), 2000);
    } finally {
      setCopying(false);
    }
  };

  const instruction = detail?.instruction ?? unit.instruction;
  const resultText = detail?.result ?? unit.result;
  const errorText = detail?.error ?? unit.error;
  // Show the copy control whenever there is preview or full text to copy.
  // Click always re-fetches full detail when needed (see handleCopy).
  const canCopy = Boolean(
    instruction || resultText || errorText || hasResultBody
  );
  const unitNumber = Number(unit.index) + 1;

  return (
    <div className={styles.unitRow}>
      {/* Header is a div (not a <button>) so instruction text stays selectable.
          Click anywhere expands; action controls stopPropagation so copy /
          chevron don't double-toggle. Chevron remains the a11y control. */}
      <div
        ref={headerRef}
        className={styles.unitRowHeader}
        onClick={handleHeaderClick}
        style={{ cursor: hasResultBody ? 'pointer' : 'default' }}
      >
        <AsyncTaskUnitStatusIcon status={unit.status} />
        <Text size="xs" c="dimmed" className={styles.unitIndex}>
          {/* The chat endpoint serves unit fields straight from a Redis hash,
              so index arrives as a STRING despite the declared type — "0" + 1
              renders "01". Coerce before the arithmetic. */}
          {unitNumber}
        </Text>
        <Text size="xs" className={styles.unitInstruction}>
          {instruction}
        </Text>
        <div
          className={styles.unitRowActions}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {canCopy ? (
            <Tooltip
              label={copied ? 'Copied' : 'Copy unit'}
              position="top"
              withArrow
            >
              <ActionIcon
                size="sm"
                variant="subtle"
                color={copied ? 'teal' : 'gray'}
                aria-label={`Copy unit ${unitNumber}`}
                {...(copying ? { loading: true } : {})}
                onClick={() => {
                  void handleCopy();
                }}
              >
                <FontAwesomeIcon icon={faCopy} style={{ fontSize: 11 }} />
              </ActionIcon>
            </Tooltip>
          ) : null}
          {hasResultBody ? (
            <Tooltip
              label={expanded ? 'Hide result' : 'Show result'}
              position="top"
              withArrow
            >
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? `Hide unit ${unitNumber} result`
                    : `Show unit ${unitNumber} result`
                }
                onClick={handleToggle}
              >
                <FontAwesomeIcon
                  icon={expanded ? faChevronDown : faChevronRight}
                  style={{ fontSize: 10 }}
                />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </div>
      </div>
      {hasResultBody && (
        <Collapse in={expanded}>
          <UnitDetailBody errorText={errorText} resultText={resultText} />
        </Collapse>
      )}
    </div>
  );
}
